import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import StepperCDS from '@sharedCR/coreDesignSystem/StepperCDS';
import PropTypes from 'prop-types';

import { getValueOrDash } from 'utils/data';
import { formatDateValue } from 'utils/format';

import { DRAW_LIFECYCLE_STEPS, DRAW_STATUS_LABEL } from '../../../../Construction.constants';
import {
  disbursementBoxStyles,
  disbursementRowStyles,
  drawReviewHeaderStyles,
  drawReviewMetaStyles,
  drawReviewPaneStyles,
} from '../../../../Construction.styles';
import { ConstructionTestIds } from '../../../../Construction.testIds';
import {
  formatMoney,
  getDrawStepperActiveStatus,
  getNetDisbursement,
} from '../../../../Construction.utils';
import { getDrawLabel } from '../../DrawListSection.utils';

const CurrentDrawReviewPane = ({ drawRequest, onOpenReview }) => {
  const statusLabel = DRAW_STATUS_LABEL[drawRequest.status];
  const submittedLabel = getValueOrDash(formatDateValue(drawRequest.requestDate));
  const netDisbursement = getNetDisbursement(drawRequest);

  const onOpenClick = useCallback(
    () => onOpenReview(drawRequest.id),
    [onOpenReview, drawRequest.id],
  );

  return (
    <Box sx={drawReviewPaneStyles}>
      <Box sx={drawReviewHeaderStyles}>
        <Box sx={drawReviewMetaStyles}>
          <Typography variant="medium16">{getDrawLabel(drawRequest)}</Typography>
          {statusLabel && (
            <LabelCDS size={LabelSize.S} title={statusLabel.title} variant={statusLabel.variant} />
          )}
          <Typography color="text.secondary" variant="regular12">
            {`Requested ${formatMoney(drawRequest.requested)} · submitted ${submittedLabel}`}
          </Typography>
        </Box>
        <Button
          data-testid={ConstructionTestIds.DRAW_REVIEW_OPEN_BUTTON}
          size="small"
          variant="outlined"
          onClick={onOpenClick}
        >
          Open Draw Review
        </Button>
      </Box>
      <StepperCDS
        activeStep={getDrawStepperActiveStatus(drawRequest.status)}
        steps={DRAW_LIFECYCLE_STEPS}
      />
      <Box sx={disbursementBoxStyles}>
        <Box sx={disbursementRowStyles}>
          <Typography color="text.secondary" variant="regular14">
            Requested this draw
          </Typography>
          <Typography variant="medium14">{formatMoney(drawRequest.requested)}</Typography>
        </Box>
        <Box sx={disbursementRowStyles}>
          <Typography color="text.secondary" variant="regular14">
            Retainage held
          </Typography>
          <Typography variant="medium14">{formatMoney(drawRequest.retainageHeld)}</Typography>
        </Box>
        <Box sx={disbursementRowStyles}>
          <Typography color="text.secondary" variant="regular14">
            Net disbursement (est.)
          </Typography>
          <Typography variant="bold16">{formatMoney(netDisbursement)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

CurrentDrawReviewPane.propTypes = {
  drawRequest: PropTypes.object.isRequired,
  onOpenReview: PropTypes.func.isRequired,
};

export default memo(CurrentDrawReviewPane);
